import { AnimationItem, LottiePlayer } from 'lottie-web';

import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostBinding, input, viewChild, output, inject, effect } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NgStyle } from '@angular/common';

import { ApThemeService } from '@ap/theme';

import { Animations, AvailableAnimations } from './animation.model';

/** Injects an animation svg */
/** Test deployment CDN */
@Component({
    selector: 'ap-animation',
    templateUrl: './animation.component.html',
    providers: [ApThemeService],
    imports: [NgStyle]
})
export class ApAnimationComponent {
    private readonly themeService = inject(ApThemeService);
    private readonly http = inject(HttpClient);

    @HostBinding('class') class = 'ap-animation';

    private lottie: LottiePlayer;

    /**
     * Animation file name (without extension)
     */
    public readonly name = input<AvailableAnimations>(undefined);

    /**
     * Width of the animation
     */
    public readonly width = input('100%');

    /**
     * Height of the animation
     */
    public readonly height = input('100%');

    /**
     * Should the animation loop?
     * Defaults to true
     */
    public readonly loop = input(true);

    /**
     * Should the animation loop?
     * Defaults to autoplay
     */
    public readonly autoplay = input(true);

    private readonly container = viewChild<ElementRef>('container');

    public loading = false;

    public readonly animationComplete = output<Event>();

    public readonly loopComplete = output<Event>();

    public readonly segmentStart = output<Event>();

    public readonly destroy = output<Event>();

    private animationItem: AnimationItem;

    constructor() {
        effect(() => {
            const name = this.name();
            if (name) {
                this.load();
            }
        });
    }

    private async load() {
        this.loading = true;
        const name = this.name();
        if (this.lottie && name) {
            await this.loadAnimation();
        } else if (name) {
            await this.loadLottie();
            await this.loadAnimation();
        }
        this.loading = false;
    }

    private async loadLottie() {
        if (!this.lottie) {
            const staticUrl = this.themeService.getStaticUrl();
            // @ts-ignore
            this.lottie = (await import(/* webpackIgnore: true */ `${staticUrl}/animation/esm/lottie.min.js`))
                .default as unknown as LottiePlayer;
        }
    }

    private async loadAnimation() {
        const { loop: loopInput, autoplay: autoplayInput } = this;
        const loop = loopInput();
        const autoplay = autoplayInput();
        const animationData = await this.loadAnimationData();
        this.animationItem?.destroy();

        try {
            this.animationItem = this.lottie.loadAnimation({
                container: this.container().nativeElement,
                renderer: 'svg', // svg html canvas
                loop,
                autoplay,
                animationData
            });
        } catch (e) {
            console.error(e);
        }
    }

    private async loadAnimationData(): Promise<any> {
        const staticUrl = this.themeService.getStaticUrl();
        const animationName = Animations[this.name().toUpperCase()];

        if (animationName) {
            return firstValueFrom(this.http.get<any>(`${staticUrl}/animation/${animationName}.json`));
        }
        console.error('not found', this.name());

        return null;
    }
}
